import { type Opportunity } from "@entities";
import { LtrNumberRatio } from "@shared/frontend/components/LtrNumberRatio";
import {
  Alert,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { type FC, useMemo } from "react";
import { getErrorMessage } from "../../pages/auth/Auth";
import { useOrganization } from "../../contexts/OrganizationProvider";
import { fetchRegistrationTracking } from "../../pages/tables/opportunities-table/registrationTrackingApi";
import {
  clampDateToOnFromChange,
  registrationTrackingDefaultDay,
  validateRegistrationDateRange,
} from "../../pages/tables/opportunities-table/registrationTrackingDateUtils";
import { usePersistedRegistrationDateRange } from "../../pages/tables/opportunities-table/usePersistedRegistrationDateRange";
import {
  StatisticsDailyLineChart,
  type DailyChartRow,
} from "../../pages/statistics/components/StatisticsDailyLineChart";
import "./EventRegistrationAnalysis.scss";

interface EventRegistrationAnalysisProps {
  eventDetails: Opportunity | undefined;
  preview?: boolean;
  tourAnchors?: boolean;
}

export const EventRegistrationAnalysis: FC<EventRegistrationAnalysisProps> = ({
  eventDetails,
}) => {
  const { currOrganization } = useOrganization();
  const { dateFrom, setDateFrom, dateTo, setDateTo } =
    usePersistedRegistrationDateRange(currOrganization?.id);
  const opportunityId = eventDetails?.id;

  const dateRangeError = useMemo(
    () => validateRegistrationDateRange(dateFrom, dateTo),
    [dateFrom, dateTo],
  );

  const queryBody = useMemo(() => {
    if (
      !currOrganization?.id ||
      !opportunityId ||
      !dateFrom ||
      !dateTo ||
      dateRangeError
    ) {
      return null;
    }
    return {
      organizationId: currOrganization.id,
      dateFrom: dateFrom.format("YYYY-MM-DD"),
      dateTo: dateTo.format("YYYY-MM-DD"),
      opportunityId,
    };
  }, [currOrganization?.id, opportunityId, dateFrom, dateTo, dateRangeError]);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["registration-tracking-detail", queryBody],
    queryFn: () => fetchRegistrationTracking(queryBody!),
    enabled: !!queryBody,
    staleTime: 1000 * 60,
  });

  const summary = data?.opportunities[0];

  const { approvedChart, interestedChart, approvedTotal, interestedTotal } =
    useMemo(() => {
      const days = data?.daily ?? [];
      const approved: DailyChartRow[] = [];
      const interested: DailyChartRow[] = [];
      let aSum = 0;
      let iSum = 0;
      for (const row of days) {
        const label = dayjs(row.date).format("DD/MM");
        aSum += row.approvedRegistrations;
        iSum += row.interestedRegistrations;
        approved.push({
          dayLabel: label,
          value: row.approvedRegistrations,
        });
        interested.push({
          dayLabel: label,
          value: row.interestedRegistrations,
        });
      }
      return {
        approvedChart: approved,
        interestedChart: interested,
        approvedTotal: aSum,
        interestedTotal: iSum,
      };
    }, [data?.daily]);

  const occupancyApproved =
    summary?.approvedTotal ?? eventDetails?.participantsCount ?? 0;
  const occupancyAmount = summary?.amount ?? eventDetails?.amount ?? 0;

  if (!eventDetails?.id) {
    return null;
  }

  return (
    <div className="event-registration-analysis">
      <p className="par">ניתוח הרשמות</p>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {eventDetails.title}
      </Typography>

      <Stack direction="row" flexWrap="wrap" gap={2} mb={2}>
        <DatePicker
          label="מתאריך"
          value={dateFrom}
          maxDate={dateTo ?? undefined}
          onChange={(value) => {
            setDateFrom(value);
            setDateTo((prev) => clampDateToOnFromChange(value, prev));
          }}
          slotProps={{ textField: { size: "small" } }}
        />
        <DatePicker
          label="עד תאריך"
          value={dateTo}
          minDate={dateFrom ?? undefined}
          maxDate={registrationTrackingDefaultDay()}
          onChange={setDateTo}
          slotProps={{ textField: { size: "small" } }}
        />
      </Stack>

      {dateRangeError && <Alert severity="warning">{dateRangeError}</Alert>}

      {isError && !dateRangeError && (
        <Alert severity="error">{getErrorMessage(error)}</Alert>
      )}

      {!dateRangeError && isLoading && (
        <Stack alignItems="center" py={4}>
          <CircularProgress />
        </Stack>
      )}

      {!dateRangeError && !isLoading && !isError && !summary && (
        <Alert severity="info">לא נמצאו נתוני הרשמה למשימה זו</Alert>
      )}

      {!dateRangeError && !isLoading && !isError && summary && (
        <>
          <Stack direction="row" flexWrap="wrap" gap={2} mb={2}>
            <Paper variant="outlined" sx={{ p: 2, minWidth: 140 }}>
              <Typography variant="caption" color="text.secondary">
                תפוסה נוכחית
              </Typography>
              <Typography variant="h6">
                <LtrNumberRatio
                  left={occupancyApproved}
                  right={occupancyAmount}
                />
              </Typography>
            </Paper>
            <Paper variant="outlined" sx={{ p: 2, minWidth: 140 }}>
              <Typography variant="caption" color="text.secondary">
                הרשמות מאושרות בטווח
              </Typography>
              <Typography variant="h6">
                {summary.registrationsInRange}
              </Typography>
            </Paper>
            <Paper variant="outlined" sx={{ p: 2, minWidth: 140 }}>
              <Typography variant="caption" color="text.secondary">
                מעוניינים בטווח
              </Typography>
              <Typography variant="h6">{summary.interestedInRange}</Typography>
            </Paper>
            <Paper variant="outlined" sx={{ p: 2, minWidth: 140 }}>
              <Typography variant="caption" color="text.secondary">
                ממתינים (סה״כ)
              </Typography>
              <Typography variant="h6">{summary.pendingTotal}</Typography>
            </Paper>
          </Stack>

          <Stack
            direction={{ xs: "column", md: "row" }}
            gap={2}
            className="charts-row"
          >
            <StatisticsDailyLineChart
              title="הרשמות מאושרות (יומי)"
              color="#29bd29"
              data={approvedChart}
              rangeTotal={approvedTotal}
            />
            <StatisticsDailyLineChart
              title="מעוניינים / ממתינים (יומי)"
              color="#065bf9"
              data={interestedChart}
              rangeTotal={interestedTotal}
            />
          </Stack>
        </>
      )}
    </div>
  );
};
